import React, { useState, useContext, useLayoutEffect } from 'react';
import { useHistory, NavLink } from 'react-router-dom';
import axios from 'axios';
import ApxDialog from 'comps/ApxDialog';
import { AppContext } from 'AppContext';
import * as QueryString from "query-string";
import ReCAPTCHA from "react-google-recaptcha";

const SignIn = (props) => {

    const { config, setIdentity } = useContext(AppContext);
    const [history] = useState(useHistory());

    const params = QueryString.parse(props.location.search);
    const [username, setUsername] = useState(params.username ? params.username : "");
    const [password, setPassword] = useState("");

    const [dialog, setDialog] = useState("");
    const [busy, setBusy] = useState(false);
    const [retries, set_retries] = useState(-1);
    const [retry_timer, set_retry_timer] = useState(-1);

    const recaptchaRef = React.useRef();

    // Workaround to make react-google-recaptcha play nice with react-router-dom
    useLayoutEffect(() => {
        return () => {
            Array.prototype.slice.call(document.getElementsByTagName('IFRAME')).forEach(element => {
                if (element.src.indexOf('www.google.com/recaptcha') > -1 && element.parentNode) {
                element.parentNode.removeChild(element);
                }
            });
        };
    }, []);

    function onSubmitWithReCAPTCHA(e) {
        setBusy(true);
        e.preventDefault();
        recaptchaRef.current.execute();
    }

    // Must use ReCAPTCHA onChange as most reliable token source
    function onSuccess(token) {
        if (token === null) {
            setBusy(false);
            recaptchaRef.current.reset();
        } else {
            executeSubmit(token);
        }
    }

    function executeSubmit(token){
        const action = async () => {
            try {
                const fields = {
                    username: username,
                    password: password,
                    token: token
                }
                const response = await axios.post(config.api.base + '/login', {...fields})
                if(response.status === 201) {
                    console.log(response.data);
                    setIdentity(response.data)

                    let redirectPath = '/dashboard';
                    if(response.data.userAdmin === true) {
                        redirectPath = '/admin/dashboard';
                    }

                    if (props.hasOwnProperty('location') && props.location !== undefined) {
                        if (props.location.hasOwnProperty('state') && props.location.state !== undefined && props.location.state !== null) {
                            if (props.location.state.hasOwnProperty('from') && props.location.state.from !== undefined) {
                                redirectPath = props.location.state.from;
                            }
                        }
                    }

                    setBusy(false);
                    executeRedirect(response.data, redirectPath);
                }

            } catch (x) {
                // setTimeout used due to open issue: https://github.com/dozoisch/react-google-recaptcha/issues/129
                setTimeout(() => recaptchaRef.current.reset(), 500);
                setBusy(false);
                if (x.response.status === 429) {
                    set_retry_timer(x.response.data.retryAfter);
                } else {
                    set_retry_timer(-1);
                }
                setDialog(x.response.data.message);
                set_retries(x.response.data.remaining);
            }
        }
       action();
    }

    function executeRedirect(responseData, redirectPath) {
        setIdentity(responseData);
        history.push(redirectPath);
    }

    function onChangeUsername(e) {
        setUsername(e.target.value);
    }

    function onChangePassword(e) {
        setPassword(e.target.value);
    }

    function onDialogOkay() {
        setDialog("");
    }

    const retries_or_retry = retries === 1 ? "retry" : "retries";
    const retry_message = "You have " + retries + " " + retries_or_retry + " remaining.";
    
    const retry_timer_mins = Math.floor(retry_timer / 60);
    const retry_timer_secs = retry_timer % 60;
    const retry_timer_message = "You may try again in " + retry_timer_mins + " minutes and " + retry_timer_secs + " seconds.";

    return(
        <div className="min-h-screen flex justify-center bg-gray-50 pt-24 px-4 sm:px-6 lg:px-8">
            <div className="max-w-md w-full space-y-8">
                <div>
                    <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
                        Sign in to your account
                    </h2>
                    <p className="mt-2 text-center text-sm text-gray-600">
                        Or&nbsp;
                        <NavLink to='/sign-up' className="font-medium text-indigo-600 hover:text-indigo-500">
                            click here to create an account
                        </NavLink>
                    </p>
                </div>
                <form className="mt-8 space-y-6" onSubmit={onSubmitWithReCAPTCHA}>
                    <div className="rounded-md shadow-sm -space-y-px">
                        <div>
                            <label htmlFor="email-address" className="sr-only">Email address</label>
                            <input id="email-address" name="email" type="email" autoComplete="email" required
                                   value={username}
                                   onChange={onChangeUsername}
                                   className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm"
                                   placeholder="Email address"/>
                        </div>
                        <div>
                            <label htmlFor="password" className="sr-only">Password</label>
                            <input id="password" name="password" type="password" autoComplete="current-password"
                                   required
                                   value={password}
                                   onChange={onChangePassword}
                                   className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-b-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm"
                                   placeholder="Password"/>
                        </div>
                    </div>
                    { retries > -1 &&
                        <div className="flex items-center text-center">
                            <p className='w-full'>{retry_message}</p>
                        </div>
                    }
                    { retry_timer > -1 &&
                        <div className="flex items-center text-center">
                            <p className='w-full'>{retry_timer_message}</p>
                        </div>
                    }
                    <div className="flex items-center justify-between">
                        <div className="flex items-center">
                            <input id="remember_me" name="remember_me" type="checkbox"
                                   className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"/>
                            <label htmlFor="remember_me" className="ml-2 block text-sm text-gray-900">
                                Remember me
                            </label>
                        </div>
                        <div className="text-sm">
                            <NavLink to='/reset-password' className="font-medium text-indigo-600 hover:text-indigo-500">
                                Forgot your password?
                            </NavLink>
                        </div>
                    </div>
                    <div>
                        <button disabled={busy} className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50">
                            Sign in
                        </button>
                    </div>
                    <ReCAPTCHA
                        ref={recaptchaRef}
                        size="invisible"
                        sitekey={config.recaptcha.key}
                        onChange={onSuccess}
                    />
                </form>
            </div>
            <ApxDialog show={!!dialog} onOkay={onDialogOkay} message={dialog}></ApxDialog>
        </div>
    )
}

export default SignIn;